import { Injectable } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { filter, take, map } from 'rxjs/operators';
import { DataStorageService } from './data-storage.service';
import { UserHelpService } from './user-help.service';
import { Repository, Structures } from '@clavisco/core';
import { IUserHelpDocumentationLink, IUserHelpDocumentationLinkEntity, UserHelpConfiguration } from '@clavisco/user-help';

/**
 * Service responsible for initializing application-wide configurations
 * after successful user authentication.
 *
 * This service follows the Single Responsibility Principle by handling
 * only post-authentication initialization tasks.
 */
@Injectable({
  providedIn: 'root'
})
export class AppInitializationService {
  private initialized = false;

  constructor(
    private router: Router,
    private dataStorageService: DataStorageService,
    private userHelpService: UserHelpService
  ) {
    this.ListenToFirstAuthenticatedNavigation();
  }

  /**
   * Listens for the first authenticated navigation after login
   * and triggers initialization tasks.
   */
  private ListenToFirstAuthenticatedNavigation(): void {
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd),
      filter(() => this.IsAuthenticated()),
      filter(() => !this.initialized),
      take(1)
    ).subscribe(() => {
      this.Initialize();
    });
  }

  /**
   * Checks if the user is authenticated by verifying the session token.
   * This is a simple check that avoids circular dependency with AuthenticationService.
   *
   * @returns true if a session exists, false otherwise.
   */
  private IsAuthenticated(): boolean {
    try {
      const session = this.dataStorageService.GetStorage('CurrentSession');
      return session !== null && session !== undefined;
    } catch {
      return false;
    }

    
  }

  /**
   * Performs all post-authentication initialization tasks.
   */
  private Initialize(): void {
    this.SetDocumentationLinkConfiguration();
    this.initialized = true;
  }

  /**
   * Resets the initialization state. Should be called on logout.
   */
  public Reset(): void {
    this.initialized = false;
  }

  /**
   * Configures the documentation links for the user-help component.
   * Sets up date formats and documentation links globally via Repository configuration.
   */
  private SetDocumentationLinkConfiguration(): void {
    this.userHelpService.Get(true)
      .pipe(
        map(response => {
          this.SetTokenConfigurationUserHelp(response);
        })
      ).subscribe();
  }

  public SetTokenConfigurationUserHelp(response: Structures.Interfaces.ICLResponse<IUserHelpDocumentationLinkEntity | IUserHelpDocumentationLinkEntity[]>): void {
    Repository.Behavior.SetTokenConfiguration({
      token: Structures.Enums.TOKENS.USER_HELP,
      value: "yyyy/MM/dd hh:mm a",
      setting: UserHelpConfiguration.DateFormats,
      override: false
    });

    Repository.Behavior.SetTokenConfiguration({
      token: Structures.Enums.TOKENS.USER_HELP,
      override: true,
      value: JSON.stringify(((response?.Data ?? []) as IUserHelpDocumentationLink[]).map(d => ({
        ApplicationPath: d.ApplicationPath,
        DocumentationLink: d.DocumentationLink,
        IsRootDocumentationLink: d.IsRootDocumentationLink
      } as IUserHelpDocumentationLink))),
      setting: UserHelpConfiguration.DocumentationLinks
    });
  }
}
